import type { Matrix3Like, Matrix4Like } from './types'

/** A 3x3 matrix; `elements` is column-major (as three's). `set` takes rows. */
export class Matrix3 {
  readonly isMatrix3 = true
  elements: number[] = [1, 0, 0, 0, 1, 0, 0, 0, 1]

  set(n11: number, n12: number, n13: number, n21: number, n22: number, n23: number, n31: number, n32: number, n33: number): this {
    const te = this.elements
    te[0] = n11; te[1] = n21; te[2] = n31
    te[3] = n12; te[4] = n22; te[5] = n32
    te[6] = n13; te[7] = n23; te[8] = n33
    return this
  }

  copy(m: Matrix3Like): this {
    for (let i = 0; i < 9; i++) this.elements[i] = m.elements[i]
    return this
  }

  /** The upper 3x3 of `m`. */
  setFromMatrix4(m: Matrix4Like): this {
    const me = m.elements
    return this.set(me[0], me[4], me[8], me[1], me[5], me[9], me[2], me[6], me[10])
  }

  /** The matrix that takes normals through `matrix4` (its upper 3x3, inverted and transposed). */
  getNormalMatrix(matrix4: Matrix4Like): this {
    return this.setFromMatrix4(matrix4).invert().transpose()
  }

  invert(): this {
    const te = this.elements,
      n11 = te[0], n21 = te[1], n31 = te[2],
      n12 = te[3], n22 = te[4], n32 = te[5],
      n13 = te[6], n23 = te[7], n33 = te[8],
      t11 = n33 * n22 - n32 * n23,
      t12 = n32 * n13 - n33 * n12,
      t13 = n23 * n12 - n22 * n13,
      det = n11 * t11 + n21 * t12 + n31 * t13
    if (det === 0) return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0)
    const detInv = 1 / det
    te[0] = t11 * detInv
    te[1] = (n31 * n23 - n33 * n21) * detInv
    te[2] = (n32 * n21 - n31 * n22) * detInv
    te[3] = t12 * detInv
    te[4] = (n33 * n11 - n31 * n13) * detInv
    te[5] = (n31 * n12 - n32 * n11) * detInv
    te[6] = t13 * detInv
    te[7] = (n21 * n13 - n23 * n11) * detInv
    te[8] = (n22 * n11 - n21 * n12) * detInv
    return this
  }

  transpose(): this {
    let tmp: number
    const m = this.elements
    tmp = m[1]; m[1] = m[3]; m[3] = tmp
    tmp = m[2]; m[2] = m[6]; m[6] = tmp
    tmp = m[5]; m[5] = m[7]; m[7] = tmp
    return this
  }
}
